/**
 * NotFoundCharacter Component
 *
 * Purpose: Handle character not found (404) scenario
 * WHO: Players navigating to invalid/deleted character
 * WHAT: Display error message and navigation options
 * WHERE: Character sheet page (error state)
 * WHY: Provide clear feedback when character doesn't exist or user lacks access
 *
 * INTEGRATION POINT: Used by CharacterSheet page on 404 error
 * Decision: Option B - Redirect to roster with toast (after showing component)
 *
 * VARIANTS:
 * - 404: Character not found
 * - 403: Access denied (no permission)
 * - Network: Connection error (retry available)
 */

import React from "react";
import {
  Center,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Divider,
  Box,
  useColorMode,
} from "@chakra-ui/react";
import { AlertCircle, Home, Users, RefreshCw, Plus, ArrowLeft } from "lucide-react";

export type NotFoundVariant = "404" | "403" | "network";

interface NotFoundCharacterProps {
  campaignId?: string;
  characterId?: string;
  variant?: NotFoundVariant;
  onRetry?: () => void;
  onBackToCampaign?: () => void;
  onBackToRoster?: () => void;
  onCreateNew?: () => void;
  onGoBack?: () => void;
}

/**
 * Error messages and guidance for each variant
 */
const ERROR_INFO: Record<NotFoundVariant, { title: string; message: string; hint: string }> = {
  "404": {
    title: "Character Not Found",
    message:
      "This character doesn't exist or has been deleted. You may no longer have access to it.",
    hint: "Possible reasons: Character was archived, removed by GM, or the link is incorrect.",
  },
  "403": {
    title: "Access Denied",
    message: "You don't have permission to view this character.",
    hint: "Only the character owner and campaign GM can view character details.",
  },
  "network": {
    title: "Connection Error",
    message: "Failed to load character data. Please check your internet connection.",
    hint: "This is usually a temporary issue. Try again in a moment.",
  },
};

export const NotFoundCharacter: React.FC<NotFoundCharacterProps> = ({
  campaignId,
  characterId,
  variant = "404",
  onRetry,
  onBackToCampaign,
  onBackToRoster,
  onCreateNew,
  onGoBack,
}) => {
  const { colorMode } = useColorMode();
  const errorInfo = ERROR_INFO[variant];

  return (
    <Center minH="60vh" py={12}>
      <Card
        maxW="500px"
        w="full"
        borderColor={variant === "network" ? "orange.200" : "red.200"}
        borderWidth={2}
        bg={colorMode === "light" ? "white" : "gray.800"}
      >
        {/* Header */}
        <CardHeader bg={variant === "network" ? "orange.50" : "red.50"} _dark={{ bg: "red.900" }}>
          <HStack spacing={3}>
            <Icon
              as={AlertCircle}
              w={6}
              h={6}
              color={variant === "network" ? "orange.500" : "red.500"}
            />
            <Heading size="md">{errorInfo.title}</Heading>
          </HStack>
        </CardHeader>

        <Divider />

        {/* Body */}
        <CardBody>
          <VStack spacing={6} align="start">
            {/* Error message */}
            <VStack spacing={2} align="start">
              <Text>{errorInfo.message}</Text>
              <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                {errorInfo.hint}
              </Text>
            </VStack>

            {/* Debug info (for development) */}
            {(characterId || campaignId) && (
              <Box
                fontSize="xs"
                color="gray.500"
                bg="gray.100"
                p={2}
                borderRadius="md"
                w="full"
                _dark={{ bg: "gray.900" }}
              >
                {characterId && <Text>Character ID: {characterId}</Text>}
                {campaignId && <Text>Campaign ID: {campaignId}</Text>}
              </Box>
            )}

            <Divider />

            {/* Action Buttons */}
            <HStack spacing={3} w="full" justify="center" flexWrap="wrap">
              {/* Retry button (for network errors) */}
              {variant === "network" && onRetry && (
                <Button
                  leftIcon={<RefreshCw size={16} />}
                  colorScheme="orange"
                  onClick={onRetry}
                >
                  Try Again
                </Button>
              )}

              {/* Back to Roster button */}
              {onBackToRoster && (
                <Button
                  leftIcon={<Users size={16} />}
                  variant="outline"
                  onClick={onBackToRoster}
                >
                  Back to Roster
                </Button>
              )}

              {/* Back to Campaign button */}
              {onBackToCampaign && (
                <Button
                  leftIcon={<Home size={16} />}
                  variant="outline"
                  onClick={onBackToCampaign}
                >
                  Back to Campaign
                </Button>
              )}
            </HStack>

            {(onCreateNew || onGoBack) && (
              <HStack spacing={3} w="full" justify="center" flexWrap="wrap">
                {onCreateNew && (
                  <Button
                    leftIcon={<Plus size={16} />}
                    colorScheme="blue"
                    onClick={onCreateNew}
                  >
                    Create Character
                  </Button>
                )}
                {onGoBack && (
                  <Button
                    leftIcon={<ArrowLeft size={16} />}
                    variant="outline"
                    onClick={onGoBack}
                  >
                    Go Back
                  </Button>
                )}
              </HStack>
            )}

            {/* Help text */}
            <Text fontSize="xs" color="gray.500" textAlign="center" w="full">
              If you think this is an error, please contact your campaign GM or check the
              link you're using.
            </Text>
          </VStack>
        </CardBody>
      </Card>
    </Center>
  );
};

export default NotFoundCharacter;
